import { goalsUpdated, setShowSuccess } from 'actions/ui';
import { Forms, Loading, Modal, SaveButton, SystemText, TitleBar } from 'e4shared';
import { getPeriodStart, periodName, sortGoalsByPeriod } from 'helpers/goals';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { apiGet, apiPost } from 'services';
import SetHistoricGoals from '../setHistoricGoals';
import posthog from 'posthog-js';

export const SetGoals = ({ show, onClose, onSave, profile, ...props }) => {
    const dispatch = useDispatch();

    const [errors, setErrors] = React.useState({});
    const [values, setValues] = React.useState({});
    const [saving, setSaving] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [periods, setPeriods] = React.useState([]);
    const [goals, setGoals] = React.useState([]);
    const [historic, setHistoric] = React.useState(null);

    const user = useSelector(state => state.user.user);

    const isMe = user?.id === profile?.id || !profile;

    const loadGoals = () => {
        setLoading(true);
        apiGet(`quota-values/${(profile || user)?.id}`, user.guid, response => {
            const temp = sortGoalsByPeriod(response);
            setGoals(temp);

            const newValues = temp.reduce((a, c) => {
                a[c.id] = c.latest_Target;
                return a;
            }, {});
            newValues.when = getPeriodStart('M');
            setValues(newValues);

            setPeriods([...new Set(temp.map(item => item.period))]);
            setLoading(false);
        });
    };

    React.useEffect(() => {
        loadGoals();
    }, [show, isMe]);

    React.useEffect(() => {
        show && props.historic && setHistoric(props.historic);
    }, [show]);

    const validate = () => {
        let valid = true;
        const temp = {};

        if (!values.when) {
            temp.when = text.fields.when.error;
            valid = false;
        }

        goals.forEach(g => {
            if (!values[g.id]) {
                temp[g.id] = text.fields.goal.error;
                valid = false;
            } else if (isNaN(values[g.id])) {
                temp[g.id] = text.fields.goal.numeric;
                valid = false;
            }
        });

        setErrors(temp);

        return valid;
    };

    const closeMe = () => {
        setErrors({});
        setValues({});
        dispatch(goalsUpdated());
        onClose();
    };

    const save = () => {
        if (validate()) {
            const allQuotas = {
                when: values.when,
                values: goals.map(g => ({ quota_Id: g.id, value: parseInt(values[g.id], 10) })),
            };

            apiPost(`quota-values/${(profile || user)?.id}`, allQuotas, user.guid, () => {
                setSaving(false);
                onSave?.();
                posthog.capture('saved-goals');
                dispatch(setShowSuccess(text.success));
                closeMe();
            });
        } else {
            setSaving(false);
        }
    };

    const text = {
        buttons: {
            save: SystemText('Goals/Edit/Buttons/Save'),
            close: SystemText('Goals/Edit/Buttons/Close'),
        },
        title: {
            me: SystemText('Goals/Edit/Title/Me'),
            them: SystemText('Goals/Edit/Title/Them', profile?.full_Name),
        },
        historic: SystemText('Goals/Edit/Historic'),
        success: SystemText('Goals/Edit/Success'),
        seconds: SystemText('Goals/Edit/Seconds'),
        fields: {
            when: {
                label: SystemText('Goals/Edit/Fields/When/Label'),
                help: SystemText('Goals/Edit/Fields/When/Help'),
                error: SystemText('Goals/Edit/Fields/When/Error'),
            },
            title: {
                year: SystemText('Goals/Edit/Fields/Title/YearGoals'),
                quarter: SystemText('Goals/Edit/Fields/Title/QuarterGoals'),
                month: SystemText('Goals/Edit/Fields/Title/MonthGoals'),
                week: SystemText('Goals/Edit/Fields/Title/WeekGoals'),
            },
            goal: {
                error: SystemText('Goals/Edit/Fields/Goal/Error'),
                numeric: SystemText('Goals/Edit/Fields/Goal/Numeric'),
            },
        },
    };

    const onChange = (x, prop) => {
        if (x > 999999999) return;
        const temp = { ...values };
        temp[prop] = x;
        setValues(temp);
    };

    const footer = (
        <div>
            <SaveButton
                data-testid="save-button"
                saving={saving}
                setSaving={setSaving}
                className="btn btn-success"
                onClick={save}
            >
                {text.buttons.save}
            </SaveButton>
            <button data-testid="close-button" className="btn" onClick={closeMe}>
                {text.buttons.close}
            </button>
        </div>
    );

    return (
        <>
            <Modal show={show && !historic} title={isMe ? text.title.me : text.title.them} close={closeMe} footer={footer}>
                <div className="form-group clear-titles" data-testid="manage-goals-form" style={{ minHeight: '240px' }}>
                    <div className="mb30">
                        <Forms.Date
                            data-testid="input-goal-date"
                            mandatory
                            extraProps={{ showMonthYearPicker: true }}
                            label={text.fields.when.label}
                            value={values.when}
                            error={errors.when}
                            onChange={x => onChange(x, 'when')}
                            help={text.fields.when.help}
                        />
                    </div>
                    {loading ? (
                        <Loading />
                    ) : (
                        <>
                            {periods.map(period => {
                                const pn = periodName(period, true);

                                const periodGoals = goals.filter(g => g.period === period);

                                return (
                                    <div className="mt20" key={`period-${period}`}>
                                        <TitleBar className="title-bar-sm" title={text.fields.title[pn.toLowerCase()]} />
                                        {periodGoals.map(goal => {
                                            const name = goal.format === 'S' ? goal.name + ` (${text.seconds})` : goal.name;
                                            return (
                                                <div className="mt10 flex flex-center" key={`goal-${goal.id}`}>
                                                    <Forms.Text
                                                        data-testid="input-goal-value"
                                                        error={errors[goal.id]}
                                                        label={name}
                                                        value={values[goal.id]}
                                                        onChange={x => onChange(x, goal.id)}
                                                        numeric
                                                        mandatory
                                                    />
                                                    <button
                                                        className="btn btn-sm btn-primary ml5 mt10"
                                                        title={text.historic}
                                                        onClick={() => setHistoric(goal)}
                                                    >
                                                        <i className="fa fa-calendar text-sm" />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })}
                        </>
                    )}
                </div>
            </Modal>
            <SetHistoricGoals
                goal={historic}
                profile={profile}
                onReload={loadGoals}
                onClose={back => {
                    setHistoric(null);
                    !back && closeMe();
                }}
            />
        </>
    );
};

export default SetGoals;
